/**
 * Blocked Territories Configuration
 * 
 * This file contains ZIP code patterns for territories that are not supported for shipping
 * within various EMEA countries.
 */

interface TerritoryBlockRule {
  locale: string;
  countryCode: string;
  territories: {
    name: string;
    zipPatterns: RegExp[];
  }[];
}

/**
 * ZIP code patterns for blocked territories by country
 */
export const BLOCKED_TERRITORIES: TerritoryBlockRule[] = [
  // Spain (es-es) - Blocked territories
  {
    locale: 'es-es',
    countryCode: 'ES',
    territories: [
      {
        name: 'Canary Islands',
        zipPatterns: [
          /^35\d{3}$/, // Las Palmas province
          /^38\d{3}$/  // Santa Cruz de Tenerife province
        ]
      },
      {
        name: 'Balearic Islands',
        zipPatterns: [
          /^07\d{3}$/  // Balearic Islands
        ]
      },
      {
        name: 'Ceuta',
        zipPatterns: [
          /^51\d{3}$/  // Ceuta
        ]
      },
      {
        name: 'Melilla',
        zipPatterns: [
          /^52\d{3}$/  // Melilla
        ]
      },
      // Note: Peñón de Vélez de la Gomera, Alhucemas Islands, Chafarinas Islands, 
      // and Alborán Island are typically covered under Ceuta/Melilla postal codes
    ]
  },
  
  // France (fr-fr) - Blocked territories
  {
    locale: 'fr-fr',
    countryCode: 'FR',
    territories: [
      {
        name: 'Guadeloupe',
        zipPatterns: [
          /^971\d{2}$/  // Guadeloupe
        ]
      },
      {
        name: 'Martinique',
        zipPatterns: [
          /^972\d{2}$/  // Martinique
        ]
      },
      {
        name: 'French Guiana',
        zipPatterns: [
          /^973\d{2}$/  // French Guiana
        ]
      },
      {
        name: 'Réunion',
        zipPatterns: [
          /^974\d{2}$/  // Réunion
        ]
      },
      {
        name: 'Mayotte',
        zipPatterns: [
          /^976\d{2}$/  // Mayotte
        ]
      },
      {
        name: 'Saint Pierre and Miquelon',
        zipPatterns: [
          /^975\d{2}$/  // Saint Pierre and Miquelon
        ]
      },
      {
        name: 'Saint Barthélemy',
        zipPatterns: [
          /^97133$/  // Saint Barthélemy
        ]
      },
      {
        name: 'Saint Martin',
        zipPatterns: [
          /^97150$/  // Saint Martin
        ]
      },
      {
        name: 'French Polynesia',
        zipPatterns: [
          /^987\d{2}$/  // French Polynesia
        ]
      },
      {
        name: 'New Caledonia',
        zipPatterns: [
          /^988\d{2}$/  // New Caledonia
        ]
      },
      {
        name: 'Wallis and Futuna',
        zipPatterns: [
          /^986\d{2}$/  // Wallis and Futuna
        ]
      },
      {
        name: 'French Southern and Antarctic Lands',
        zipPatterns: [
          /^984\d{2}$/  // French Southern and Antarctic Lands
        ]
      }
    ]
  },
  
  // United Kingdom (en-gb) - Blocked territories
  {
    locale: 'en-gb',
    countryCode: 'GB',
    territories: [
      {
        name: 'Gibraltar',
        zipPatterns: [
          /^GX11\s*1AA$/i  // Gibraltar
        ]
      },
      {
        name: 'Falkland Islands',
        zipPatterns: [
          /^FIQQ\s*1ZZ$/i  // Falkland Islands
        ]
      },
      {
        name: 'South Georgia and the South Sandwich Islands',
        zipPatterns: [
          /^SIQQ\s*1ZZ$/i  // South Georgia and the South Sandwich Islands
        ]
      },
      {
        name: 'British Indian Ocean Territory',
        zipPatterns: [
          /^BBND\s*1ZZ$/i  // British Indian Ocean Territory
        ]
      },
      {
        name: 'Turks and Caicos Islands',
        zipPatterns: [
          /^TKCA\s*1ZZ$/i  // Turks and Caicos Islands
        ]
      },
      {
        name: 'Anguilla',
        zipPatterns: [
          /^AI-2640$/i  // Anguilla
        ]
      },
      {
        name: 'Bermuda',
        zipPatterns: [
          /^[A-Z]{2}\s*\d{2}$/i  // Bermuda (e.g., HM 12)
        ]
      },
      {
        name: 'British Virgin Islands',
        zipPatterns: [
          /^VG\d{4}$/i  // British Virgin Islands
        ]
      },
      {
        name: 'Cayman Islands',
        zipPatterns: [
          /^KY\d-\d{4}$/i  // Cayman Islands (e.g., KY1-1234)
        ]
      },
      {
        name: 'Montserrat',
        zipPatterns: [
          /^MSR\s*\d{4}$/i  // Montserrat
        ]
      },
      {
        name: 'Pitcairn Islands',
        zipPatterns: [
          /^PCRN\s*1ZZ$/i  // Pitcairn Islands
        ]
      },
      {
        name: 'Saint Helena, Ascension and Tristan da Cunha',
        zipPatterns: [
          /^(STHL|ASCN|TDCU)\s*1ZZ$/i  // Saint Helena, Ascension, Tristan da Cunha
        ]
      },
      {
        name: 'Sovereign Base Areas of Akrotiri and Dhekelia',
        zipPatterns: [
          /^BFPO\s*57$/i,  // Akrotiri
          /^BFPO\s*58$/i   // Dhekelia
        ]
      },
      {
        name: 'British Antarctic Territory',
        zipPatterns: [
          /^BIQQ\s*1ZZ$/i  // British Antarctic Territory
        ]
      }
    ]
  },
  
  // Denmark (da-dk) - Blocked territories
  {
    locale: 'da-dk',
    countryCode: 'DK',
    territories: [
      {
        name: 'Greenland',
        zipPatterns: [
          /^39\d{2}$/  // Greenland
        ]
      },
      {
        name: 'Faroe Islands',
        zipPatterns: [
          /^FO-?\d{3}$/i  // Faroe Islands (e.g., FO-100 or FO100)
        ]
      }
    ]
  },
  
  // Netherlands (nl-nl) - Blocked territories
  {
    locale: 'nl-nl',
    countryCode: 'NL',
    territories: [
      {
        name: 'Aruba',
        zipPatterns: [
          /^AW$/i  // Aruba (uses country code as postal code)
        ]
      },
      {
        name: 'Curaçao',
        zipPatterns: [
          /^CW$/i  // Curaçao (uses country code as postal code)
        ]
      },
      {
        name: 'Sint Maarten',
        zipPatterns: [
          /^SX$/i  // Sint Maarten (uses country code as postal code)
        ]
      },
      {
        name: 'Bonaire',
        zipPatterns: [
          /^BQ$/i  // Bonaire (Caribbean Netherlands)
        ]
      },
      {
        name: 'Sint Eustatius',
        zipPatterns: [
          /^BQ$/i  // Sint Eustatius (Caribbean Netherlands)
        ]
      },
      {
        name: 'Saba',
        zipPatterns: [
          /^BQ$/i  // Saba (Caribbean Netherlands)
        ]
      }
    ]
  }
];

/**
 * Also check for en-eu locale as it includes some of these countries
 */
export const EN_EU_BLOCKED_TERRITORIES: TerritoryBlockRule[] = [
  // Netherlands territories for en-eu locale
  {
    locale: 'en-eu',
    countryCode: 'NL',
    territories: [
      {
        name: 'Aruba',
        zipPatterns: [
          /^AW$/i
        ]
      },
      {
        name: 'Curaçao',
        zipPatterns: [
          /^CW$/i
        ]
      },
      {
        name: 'Sint Maarten',
        zipPatterns: [
          /^SX$/i
        ]
      },
      {
        name: 'Bonaire',
        zipPatterns: [
          /^BQ$/i
        ]
      },
      {
        name: 'Sint Eustatius',
        zipPatterns: [
          /^BQ$/i
        ]
      },
      {
        name: 'Saba',
        zipPatterns: [
          /^BQ$/i
        ]
      }
    ]
  }
];

/**
 * Validates if a ZIP code belongs to a blocked territory
 * 
 * @param zipCode - The postal/ZIP code to validate
 * @param countryCode - The ISO country code (e.g., 'ES', 'FR', 'GB')
 * @param locale - The current locale (e.g., 'es-es', 'fr-fr', 'en-gb')
 * @returns Object with isBlocked flag, territory name, and error message key if blocked
 */
export const validateBlockedTerritory = (
  zipCode: string,
  countryCode: string,
  locale: string
): { isBlocked: boolean; territoryName?: string; errorMessageKey?: string } => {
  if (!zipCode || !countryCode || !locale) {
    return { isBlocked: false };
  }

  // Normalize the ZIP code (remove extra spaces, convert to uppercase for comparison)
  const normalizedZipCode = zipCode.trim().toUpperCase();
  
  // Combine main blocked territories with en-eu specific ones
  const allBlockedTerritories = [...BLOCKED_TERRITORIES, ...EN_EU_BLOCKED_TERRITORIES];
  
  // Find rules for the current locale and country
  const applicableRules = allBlockedTerritories.filter(
    rule => rule.locale === locale && rule.countryCode === countryCode
  );
  
  // Check each applicable rule
  for (const rule of applicableRules) {
    for (const territory of rule.territories) {
      for (const pattern of territory.zipPatterns) {
        if (pattern.test(normalizedZipCode)) {
          return {
            isBlocked: true,
            territoryName: territory.name,
            errorMessageKey: 'REGION_NOT_SUPPORTED'
          };
        }
      }
    }
  }
  
  return { isBlocked: false };
};

/**
 * Gets all blocked territories for a specific locale and country
 * 
 * @param locale - The current locale
 * @param countryCode - The ISO country code
 * @returns Array of blocked territory names
 */
export const getBlockedTerritoriesForCountry = (
  locale: string,
  countryCode: string
): string[] => {
  const allBlockedTerritories = [...BLOCKED_TERRITORIES, ...EN_EU_BLOCKED_TERRITORIES];
  
  const applicableRules = allBlockedTerritories.filter(
    rule => rule.locale === locale && rule.countryCode === countryCode
  );
  
  const territories: string[] = [];
  applicableRules.forEach(rule => {
    rule.territories.forEach(territory => {
      if (!territories.includes(territory.name)) {
        territories.push(territory.name);
      }
    });
  });
  
  return territories;
};
